import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faPaperPlane, faCheckCircle, faTimesCircle, faCommentDots } from '@fortawesome/pro-regular-svg-icons';
import { faLoader } from '@fortawesome/pro-solid-svg-icons';

const FurnitureInventoryManager = ({ unitId, bookingId, onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sendingLink, setSendingLink] = useState(false);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
    const [message, setMessage] = useState(null);
    const [signatureStatus, setSignatureStatus] = useState(null);
    const [generatedLink, setGeneratedLink] = useState(null);

    useEffect(() => {
        if (unitId) {
            loadInventoryItems();
        }
        if (bookingId) {
            checkSignatureStatus();
        }
    }, [unitId, bookingId]);

    const loadInventoryItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}furniture-inventory/unit/${unitId}/items`
            );
            
            if (response.data.success) {
                if (response.data.items.length > 0) {
                    setItems(response.data.items);
                } else {
                    // Initialize with default C&C items
                    setItems(getDefaultCCItems());
                }
            }
        } catch (err) {
            console.error('Error loading inventory items:', err);
            setMessage({ type: 'error', text: 'Failed to load inventory items' });
            // Initialize with default items on error
            setItems(getDefaultCCItems());
        } finally {
            setLoading(false);
        }
    };

    const checkSignatureStatus = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}furniture-inventory/booking/${bookingId}/status`
            );
            if (response.data.success) {
                setSignatureStatus(response.data);
            }
        } catch (err) {
            console.error('Error checking signature status:', err);
        }
    };

    const getDefaultCCItems = () => {
        return [
            { item_name: 'Queen - Mattress and Base', quantity: 1, description: '', sort_order: 0 },
            { item_name: 'Mattress protector', quantity: 1, description: '', sort_order: 1 },
            { item_name: 'Base cover', quantity: 2, description: '', sort_order: 2 },
            { item_name: 'Curtains', quantity: 2, description: 'Unit 612,613,617,618', sort_order: 3 },
            { item_name: 'Blinds', quantity: 2, description: 'Unit 622 and 623', sort_order: 4 },
            { item_name: 'Kitchen nook stools', quantity: 2, description: '', sort_order: 5 },
            { item_name: 'Fridge/Freezer', quantity: 1, description: '', sort_order: 6 },
            { item_name: 'Microwave', quantity: 1, description: '', sort_order: 7 },
            { item_name: 'Kitchen Bin', quantity: 1, description: '', sort_order: 8 },
            { item_name: 'Kettle & toaster combo', quantity: 1, description: '', sort_order: 9 },
            { item_name: 'TV and wall bracket', quantity: 1, description: '', sort_order: 10 },
            { item_name: '2 seater couch', quantity: 1, description: 'Unit 617, 618', sort_order: 11 },
            { item_name: 'Dining table', quantity: 1, description: 'Unit 617, 618', sort_order: 12 },
            { item_name: 'Dining chairs', quantity: 2, description: 'Unit 617, 618', sort_order: 13 },
            { item_name: 'Patio Table', quantity: 1, description: 'Unit 612,613', sort_order: 14 },
            { item_name: 'Patio Chairs', quantity: 2, description: 'Unit 612,613', sort_order: 15 },
        ];
    };

    const addItem = () => {
        setItems([...items, { 
            item_name: '', 
            quantity: 1, 
            description: '', 
            sort_order: items.length 
        }]);
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const removeItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    const saveItems = async () => {
        try {
            setSaving(true);
            setMessage(null);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}furniture-inventory/items`,
                {
                    unit_id: unitId,
                    items: items.filter(item => item.item_name.trim() !== ''),
                }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'Inventory items saved successfully' });
                setItems(response.data.items);
            } else {
                setMessage({ type: 'error', text: response.data.message || 'Failed to save items' });
            }
        } catch (err) {
            console.error('Error saving items:', err);
            setMessage({ type: 'error', text: 'Failed to save inventory items' });
        } finally {
            setSaving(false);
        }
    };

    const generateAndSendLink = async () => {
        try {
            setSendingLink(true);
            setMessage(null);

            // First, save items if there are any changes
            await saveItems();

            // Generate link
            const linkResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}furniture-inventory/generate-link`,
                { booking_id: bookingId }
            );

            if (!linkResponse.data.success) {
                setMessage({ type: 'error', text: linkResponse.data.message || 'Failed to generate link' });
                return;
            }

            const { link, link_url } = linkResponse.data;

            // Create pending signature record
            await axios.post(
                `${process.env.REACT_APP_API_URL}furniture-inventory/create-pending`,
                { booking_id: bookingId, link_url }
            );

            setGeneratedLink(link);
            setMessage({ 
                type: 'success', 
                text: `Link generated successfully! You can now send this link to the tenant.` 
            });

            // Refresh signature status
            checkSignatureStatus();
        } catch (err) {
            console.error('Error generating link:', err);
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.message || 'Failed to generate link' 
            });
        } finally {
            setSendingLink(false);
        }
    };

    const sendViaWhatsApp = async () => {
        if (!generatedLink) {
            setMessage({ type: 'error', text: 'Please generate a link first' });
            return;
        }

        try {
            setSendingWhatsApp(true);
            setMessage(null);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}furniture-inventory/send-link`,
                {
                    booking_id: bookingId,
                    link: generatedLink,
                }
            );

            if (response.data.success) {
                setMessage({ 
                    type: 'success', 
                    text: 'Furniture inventory link sent via WhatsApp successfully!' 
                });
            } else {
                setMessage({ 
                    type: 'error', 
                    text: response.data.message || 'Failed to send link' 
                });
            }
        } catch (err) {
            console.error('Error sending WhatsApp:', err);
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.message || 'Failed to send link via WhatsApp' 
            });
        } finally {
            setSendingWhatsApp(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faLoader} className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Furniture Inventory Manager</h2>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Signature Status */}
            {signatureStatus && (
                <div className={`mb-6 p-4 rounded-lg ${
                    signatureStatus.signed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                }`}>
                    <div className="flex items-center gap-2">
                        {signatureStatus.signed ? (
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-600" />
                                <span className="font-semibold text-green-900">
                                    Inventory signed on {new Date(signatureStatus.signature.signed_at).toLocaleDateString()}
                                </span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5 text-yellow-600" />
                                <span className="font-semibold text-yellow-900">
                                    Inventory not yet signed by tenant
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Message */}
            {message && (
                <div className={`mb-6 p-4 rounded-lg ${
                    message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                    {message.text}
                </div>
            )}

            {/* Items Table */}
            <div className="overflow-x-auto mb-6">
                <table className="w-full">
                    <thead>
                        <tr className="border-b-2 border-gray-300">
                            <th className="text-left py-2 px-2 font-semibold text-gray-700">Item Name</th>
                            <th className="text-center py-2 px-2 font-semibold text-gray-700 w-24">Quantity</th>
                            <th className="text-left py-2 px-2 font-semibold text-gray-700">Description</th>
                            <th className="w-16"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index} className="border-b border-gray-200">
                                <td className="py-2 px-2">
                                    <input
                                        type="text"
                                        value={item.item_name}
                                        onChange={(e) => updateItem(index, 'item_name', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Item name"
                                    />
                                </td>
                                <td className="py-2 px-2">
                                    <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                    />
                                </td>
                                <td className="py-2 px-2">
                                    <input
                                        type="text"
                                        value={item.description || ''}
                                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Optional description"
                                    />
                                </td>
                                <td className="py-2 px-2 text-center">
                                    <button
                                        onClick={() => removeItem(index)}
                                        className="text-red-600 hover:text-red-800 p-1"
                                        title="Remove item"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Generated Link Display */}
            {generatedLink && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Generated Link:</p>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={generatedLink}
                            readOnly
                            className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded text-sm"
                        />
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(generatedLink);
                                setMessage({ type: 'success', text: 'Link copied to clipboard!' });
                            }}
                            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                        >
                            Copy
                        </button>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 flex-wrap">
                <button
                    onClick={addItem}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    Add Item
                </button>

                <button
                    onClick={saveItems}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                        <>
                            <FontAwesomeIcon icon={faLoader} className="w-4 h-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
                            Save Items
                        </>
                    )}
                </button>

                {bookingId && (
                    <>
                        <button
                            onClick={generateAndSendLink}
                            disabled={sendingLink || saving}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                        >
                            {sendingLink ? (
                                <>
                                    <FontAwesomeIcon icon={faLoader} className="w-4 h-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
                                    Generate Link
                                </>
                            )}
                        </button>

                        {generatedLink && (
                            <button
                                onClick={sendViaWhatsApp}
                                disabled={sendingWhatsApp || !generatedLink}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {sendingWhatsApp ? (
                                    <>
                                        <FontAwesomeIcon icon={faLoader} className="w-4 h-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCommentDots} className="w-4 h-4" />
                                        Send via WhatsApp
                                    </>
                                )}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default FurnitureInventoryManager;
